// native
import { Directive, Input, Injectable } from '@angular/core';
import { Subject } from 'rxjs';

// models
import { CompositeFilterDescriptor, FilterDescriptor } from 'app/models';

/**
 * Per-grid service that filter cells use to push an updated composite filter
 * back to the grid. Mirrors the contract of Kendo's `FilterService` so the
 * existing custom filter cells need only swap their imports.
 */
@Injectable()
export class GridFilterService {
  public readonly changes = new Subject<CompositeFilterDescriptor>();

  public filter(value: CompositeFilterDescriptor): void {
    this.changes.next(value);
  }
}

/**
 * Base class for filter cell components. Re-implements the small surface of
 * Kendo's `BaseFilterCellComponent` that the app relies on:
 * `filterByField`, `updateFilter`, `removeFilter` and `applyFilter`.
 */
@Directive()
export abstract class BaseFilterCellComponent {
  @Input() filter: CompositeFilterDescriptor;

  constructor(protected filterService: GridFilterService) { }

  public filterByField(field: string): FilterDescriptor | null {
    const found = (this.filter?.filters as FilterDescriptor[] | undefined)
      ?.find(f => (f as FilterDescriptor)?.field === field);
    return (found as FilterDescriptor) ?? null;
  }

  protected updateFilter(descriptor: FilterDescriptor): CompositeFilterDescriptor {
    const filters = ((this.filter?.filters as FilterDescriptor[]) ?? [])
      .filter(f => f.field !== descriptor.field);
    filters.push(descriptor);
    return { logic: this.filter?.logic ?? 'and', filters };
  }

  protected removeFilter(field: string): CompositeFilterDescriptor {
    return {
      logic: this.filter?.logic ?? 'and',
      filters: ((this.filter?.filters as FilterDescriptor[]) ?? []).filter(f => f.field !== field)
    };
  }

  protected applyFilter(filter: CompositeFilterDescriptor): void {
    this.filterService.filter(filter);
  }
}
